import * as THREE from 'three';
import { Ctx } from './core/ctx.js';
import { makeConfig } from './core/config.js';
import { Input } from './core/input.js';
import { DevOverlay } from './core/devoverlay.js';
import { FIXED_DT } from './core/time.js';
import { Renderer } from './render/renderer.js';

/**
 * Boot and the frame loop.
 *
 * Loop order is fixed and every subsystem opts into the phases it needs:
 *
 *   fixedUpdate(h)  120 Hz, deterministic — physics, AI, ballistics, cooldowns
 *   update(dt)      per render frame — camera, viewmodel, particles, HUD
 *   lateUpdate(dt)  after all updates — attachments, IK, anything that must
 *                   read a transform another system wrote this frame
 *   render()        the draw
 *
 * Nothing that affects gameplay outcome may live in `update` — at 30 fps versus
 * 144 fps it would run a different number of times and the game would literally
 * play differently on different machines.
 */

const boot = {
  el: document.getElementById('boot-status'),
  fill: document.getElementById('boot-fill'),
  veil: document.getElementById('veil'),
  start: document.getElementById('start'),
  say(msg, pct) {
    if (this.el) this.el.textContent = msg;
    if (this.fill && pct != null) this.fill.style.width = `${Math.round(pct * 100)}%`;
    // Yield so the browser actually paints the progress update between steps.
    return new Promise((r) => requestAnimationFrame(() => r()));
  },
};

async function main() {
  const canvas = document.getElementById('view');
  const config = makeConfig();

  await boot.say('creating context', 0.05);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(config.CAMERA.fov, 1, 0.05, 2000);

  // The viewmodel lives in its own scene/camera. See render/renderer.js for why.
  const viewScene = new THREE.Scene();
  const viewCamera = new THREE.PerspectiveCamera(62, 1, 0.01, 12);
  // The viewmodel and its lights are parented to this camera so they stay
  // locked to the screen; a camera only contributes its children's transforms
  // if it is itself part of the rendered graph.
  viewScene.add(viewCamera);

  const ctx = new Ctx({ scene, camera, viewScene, viewCamera, canvas, config });
  ctx.register('renderer', new Renderer(ctx));
  ctx.input = new Input(canvas, ctx.events, { requireLock: !config.capture });

  const dev = new DevOverlay(ctx);

  // ---- subsystem registration -------------------------------------------
  // Order matters only for init; at runtime everything resolves through ctx.get.
  const mods = [];
  const add = async (id, loader, label, pct) => {
    await boot.say(label, pct);
    const Mod = await loader();
    const inst = new Mod(ctx);
    ctx.register(id, inst);
    mods.push({ id, inst });
    return inst;
  };

  await add('materials', async () => (await import('./materials/library.js')).MaterialLibrary, 'baking materials', 0.12);
  await add('sky',       async () => (await import('./sky/atmosphere.js')).Sky,                'building sky', 0.24);
  await add('physics',   async () => (await import('./physics/world.js')).PhysicsWorld,        'physics', 0.32);
  await add('fx',        async () => (await import('./fx/index.js')).Fx,                       'effects', 0.40);
  await add('audio',     async () => (await import('./audio/index.js')).Audio,                 'synthesizing audio', 0.48);
  await add('world',     async () => (await import('./world/level.js')).Level,                 'raising the cosmodrome', 0.58);
  await add('player',    async () => (await import('./player/index.js')).Player,               'player', 0.72);
  await add('weapons',   async () => (await import('./weapons/index.js')).Weapons,             'forging Ironwake', 0.80);
  await add('ai',        async () => (await import('./ai/index.js')).AI,                       'waking the fallen', 0.87);
  await add('ui',        async () => (await import('./ui/index.js')).UI,                       'hud', 0.92);
  await add('director',  async () => (await import('./world/encounters.js')).Director,         'encounters', 0.95);

  await boot.say('compiling shaders', 0.97);
  const { Composer } = await import('./render/post/composer.js');
  const composer = new Composer(ctx);
  ctx.register('composer', composer);
  ctx.get('renderer').setComposer(composer);
  ctx.get('renderer').resize();

  // Prewarm: compile every material variant before frame 1. Without this the
  // first shot, the first enemy death and the first decal each trigger a shader
  // compile mid-gameplay, which stalls for hundreds of milliseconds.
  const { prewarm } = await import('./render/prewarm.js');
  await prewarm(ctx, mods);

  await boot.say('ready', 1);

  // ---- phase dispatch tables --------------------------------------------
  // Collected once so the hot loop iterates dense arrays instead of testing for
  // method existence on every module every frame.
  const fixedList = mods.filter((m) => typeof m.inst.fixedUpdate === 'function').map((m) => m.inst);
  const updateList = mods.filter((m) => typeof m.inst.update === 'function').map((m) => m.inst);
  const lateList = mods.filter((m) => typeof m.inst.lateUpdate === 'function').map((m) => m.inst);

  const renderer = ctx.get('renderer');
  const time = ctx.time;
  const input = ctx.input;

  let running = false;

  function frame(now) {
    requestAnimationFrame(frame);
    if (!running) return;

    const steps = time.beginFrame(now);

    if (!ctx.state.paused) {
      for (let s = 0; s < steps; s++) {
        input.updateAxes();
        for (let i = 0; i < fixedList.length; i++) fixedList[i].fixedUpdate(FIXED_DT, ctx);
        input.endStep();
        time.advanceTick();
      }
    } else {
      // Still drain look input while paused so the cursor does not snap on resume.
      input.mouseDX = 0;
      input.mouseDY = 0;
    }

    const dt = time.dt;
    for (let i = 0; i < updateList.length; i++) updateList[i].update(dt, ctx);
    for (let i = 0; i < lateList.length; i++) lateList[i].lateUpdate(dt, ctx);

    renderer.render();
    dev.update(time.rawDt);
  }

  requestAnimationFrame(frame);

  const begin = () => {
    if (running) return;
    running = true;
    boot.veil?.classList.add('gone');
    ctx.get('audio').unlock();
    // Pointer lock requires a real user gesture and a top-level document; in the
    // headless capture harness neither holds, and requesting it throws.
    if (!config.capture) input.requestLock();
    ctx.events.emit('game:start', {});
  };

  if (config.capture) {
    // Headless harness: start immediately, no click gate, no pointer lock.
    begin();
    globalThis.__claustiny = { ctx, begin, renderer };
  } else {
    boot.start?.classList.add('ready');
    boot.veil?.addEventListener('click', begin);
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Enter' || e.code === 'Space') begin();
    }, { once: true });
    globalThis.__claustiny = { ctx, begin, renderer };
  }

  // Clicking the canvas after ESC re-acquires the pointer.
  if (!config.capture) {
    canvas.addEventListener('click', () => {
      if (running && !input.locked && !ctx.state.missionComplete) input.requestLock();
    });

    ctx.events.on('input:lock', ({ locked }) => {
      // Losing the pointer pauses, unless the mission already ended.
      if (!ctx.state.missionComplete && !ctx.state.playerDead) {
        ctx.state.paused = !locked;
        ctx.events.emit('game:pause', { paused: ctx.state.paused });
      }
    });
  }
}

main().catch((err) => {
  console.error(err);
  const el = document.getElementById('boot-status');
  if (el) {
    el.style.color = '#ff8080';
    el.textContent = `boot failed: ${err.message}`;
  }
});
